import React, { useState, useCallback } from 'react';
import {
  Table,
  TableColumn,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  Box,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Grid,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import RestoreIcon from '@material-ui/icons/Restore';
import SearchIcon from '@material-ui/icons/Search';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { TransactionGroup, PerformanceSortOption, PERFORMANCE_SORT_LABELS, PaginationInfo } from '../../types';

const useStyles = makeStyles(theme => ({
  filterCard: {
    marginBottom: theme.spacing(2),
  },
  searchField: {
    minWidth: 200,
    flexGrow: 1,
  },
  selectField: {
    minWidth: 150,
  },
  transactionTitle: {
    fontWeight: 500,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  operationText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontFamily: 'monospace',
  },
  durationText: {
    fontWeight: 'bold',
    textAlign: 'right',
  },
  countText: {
    fontWeight: 'bold',
    textAlign: 'right',
  },
  paginationContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  slowTransaction: {
    color: theme.palette.error.main,
  },
  mediumTransaction: {
    color: theme.palette.warning.main,
  },
  fastTransaction: {
    color: theme.palette.success.main,
  },
}));

type PerformanceListComponentProps = {
  transactionGroups: TransactionGroup[];
  isLoading: boolean;
  error: Error | undefined;
  currentFilters: Record<string, string>;
  pagination?: PaginationInfo;
  onFilterChange: (filters: Record<string, string>) => void;
  onSearch: (query: string) => void;
  onDateRangeChange: (start: string | undefined, end: string | undefined) => void;
  onRefresh: () => void;
  onTransactionClick: (transaction: TransactionGroup) => void;
  onPageChange: (cursor: string | undefined) => void;
  host: string;
  org: string;
  projectId: string;
};

export const PerformanceListComponent = ({
  transactionGroups,
  isLoading,
  error,
  currentFilters,
  pagination,
  onFilterChange,
  onSearch,
  onDateRangeChange,
  onRefresh,
  onTransactionClick,
  onPageChange,
}: PerformanceListComponentProps) => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
//   const [startDate, setStartDate] = useState('');
//   const [endDate, setEndDate] = useState('');

  const handleSearchSubmit = useCallback(() => {
    onSearch(searchQuery);
  }, [searchQuery, onSearch]);

//   const handleDateRangeSubmit = useCallback(() => {
//     const start = startDate ? new Date(startDate).toISOString() : undefined;
//     const end = endDate ? new Date(endDate).toISOString() : undefined;
//     onDateRangeChange(start, end);
//   }, [startDate, endDate, onDateRangeChange]);

  const handlePageSizeChange = useCallback((newLimit: number) => {
    onFilterChange({ limit: newLimit.toString() });
  }, [onFilterChange]);

  const handleReset = useCallback(() => {
    setSearchQuery('');
    // setStartDate('');
    // setEndDate('');
    const resetFilters: Record<string, string> = { 
      sort: PerformanceSortOption.NEWEST, 
      limit: '10',
    };
    onFilterChange(resetFilters);
    onSearch('');
    onDateRangeChange(undefined, undefined);
  }, [onFilterChange, onSearch, onDateRangeChange]);

  // Helper function to format duration
  const formatDuration = (ms: number): string => {
    if (ms === 0) return '0ms';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  // Helper function to get duration color class
  const getDurationColorClass = (avgDuration: number): string => {
    if (avgDuration > 1000) return classes.slowTransaction; // > 1s is slow
    if (avgDuration > 500) return classes.mediumTransaction; // > 500ms is medium
    return classes.fastTransaction; // <= 500ms is fast
  };

  const columns: TableColumn[] = [
    {
      title: 'Transaction',
      field: 'transaction',
      sorting: false,
      width: '40%',
    },
    {
      title: 'Operation',
      field: 'operation',
      sorting: false,
      width: '25%',
    },
    {
      title: 'Avg Duration',
      field: 'avgDuration',
      sorting: false,
      width: '15%',
      align: 'right',
    },
    {
      title: 'Count',
      field: 'count',
      sorting: false,
      width: '15%',
      align: 'right',
    },
  ];

  const data = transactionGroups.map(transaction => ({
    transaction: (
      <Typography
        className={classes.transactionTitle}
        component="button"
        onClick={() => onTransactionClick(transaction)}
        style={{ background: 'none', border: 'none', textAlign: 'left', width: '100%' }}
      >
        {transaction.transaction}
      </Typography>
    ),
    operation: (
      <Typography className={classes.operationText}>
        {transaction.op}
        {transaction.method && ` (${transaction.method})`}
      </Typography>
    ),
    avgDuration: (
      <Typography 
        className={`${classes.durationText} ${getDurationColorClass(transaction.avgDuration)}`}
      >
        {formatDuration(transaction.avgDuration)}
      </Typography>
    ),
    count: (
      <Typography className={classes.countText}>
        {transaction.transactionCount.toLocaleString()}
      </Typography>
    ),
  }));

  if (isLoading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <Box>
      {/* Filters Card */}
      <Card className={classes.filterCard}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Date Range */}
            {/* <Grid item xs={12} md={2}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDateRangeSubmit}
                fullWidth
              >
                Filter
              </Button>
            </Grid> */}

            {/* Search */}
            <Grid item xs={12} md={3}>
              <TextField
                className={classes.searchField}
                label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleSearchSubmit} size="small">
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
                fullWidth
              />
            </Grid>

            {/* Sort */}
            <Grid item xs={6} md={1}>
              <FormControl className={classes.selectField} fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={currentFilters.sort || PerformanceSortOption.NEWEST}
                  onChange={(e) => onFilterChange({ sort: e.target.value as string })}
                >
                  {Object.entries(PERFORMANCE_SORT_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Page Size */}
            <Grid item xs={1} md={1}>
              <FormControl className={classes.selectField} fullWidth>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={parseInt(currentFilters.limit || '10')}
                  onChange={(e) => handlePageSizeChange(e.target.value as number)}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Actions */}
            <Grid item xs={1} md={1}>
              <Tooltip title="Reset Filters">
                <IconButton onClick={handleReset} color="default">
                  <RestoreIcon />
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid item xs={1} md={6}>
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <Tooltip title="Refresh">
                  <IconButton onClick={onRefresh} color="primary">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Performance Table */}
      <Table
        options={{
          search: false,
          paging: false,
          padding: 'dense',
          toolbar: false,
        }}
        title={`Performance (${transactionGroups.length})`}
        columns={columns}
        data={data}
      />

      {/* Custom Cursor Pagination */}
      <Paper className={classes.paginationContainer}>
        <Typography variant="body2" color="textSecondary">
          Showing {transactionGroups.length} transactions
        </Typography>
        
        <div className={classes.paginationControls}>
          <Button
            size="small"
            startIcon={<NavigateBeforeIcon />}
            onClick={() => onPageChange(pagination?.previous)}
            disabled={!pagination?.previous || isLoading}
            variant="outlined"
          >
            Previous
          </Button>
          
          <Button
            size="small"
            endIcon={<NavigateNextIcon />}
            onClick={() => onPageChange(pagination?.next)}
            disabled={!pagination?.next || isLoading}
            variant="outlined"
          >
            Next
          </Button>
        </div>
      </Paper>
    </Box>
  );
};